/**
 * 品牌（公司主体）索引
 *
 * 各品牌的文案（appName / companyName / 联系方式）放在语言文件里，按品牌分组维护：
 *   src/i18n/<lang>.js  →  common: { qy: {...}, cm: {...} }
 * 这样每种语言都能有自己的公司名与地址译文；本文件只负责：
 *   1) 品牌 key 与别名（URL 参数 appname 用什么值能匹配到哪个品牌）
 *   2) 默认品牌
 *   3) 把选中品牌的字段扁平化到 common 顶层，供 {{common.appName}} 这类占位符使用
 *
 * URL 示例：
 *   ?lang=zh-CN&type=user&appname=qy            → 轻语Ai
 *   ?lang=ms&type=user&appname=蚕猫ai           → 蚕猫ai
 */
export const defaultBrand = 'qy'

// 品牌 key -> 可匹配的名称（忽略大小写与空格）
export const brandAliases = {
  qy: ['qy', '轻语Ai', '轻语AI', '轻语', 'Qingyu Ai', 'QingYuAi', 'qingyuai'],
  cm: ['cm', '蚕猫ai', '蚕猫AI', '蚕猫', 'CanMaoAi', 'canmaoai'],
  sf: ['sf', '树帆科技', '树帆', 'ShuFan', 'ShuFanTech', 'shufan'],
  umeipires: ['umeipires', 'Umeipires', 'umeipiresAI', 'um'],
  sjxf: ['sjxf', '时间小方', '时间小方TK', 'TK', 'tk', 'shijianxiaofang']
}

export function normalizeBrand(value) {
  return String(value == null ? '' : value).replace(/\s+/g, '').toLowerCase()
}

/**
 * 按品牌名匹配品牌 key
 * @param {string} name URL 参数 appname 的值，如「蚕猫ai」「cm」
 * @returns {string|null} 品牌 key（qy / cm），未匹配返回 null
 */
export function matchBrand(name) {
  const target = normalizeBrand(name)
  if (!target) return null
  const keys = Object.keys(brandAliases)
  return keys.find(key => normalizeBrand(key) === target) ||
    keys.find(key => brandAliases[key].some(alias => normalizeBrand(alias) === target)) ||
    null
}

/**
 * 把 common 中「选中品牌」的字段提升到 common 顶层
 * - 兼容旧格式（common 里直接就是 appName 等字段）：原样返回
 * - 新格式（qy / cm 分组）：取 defaultBrand 兜底，再用选中品牌逐字段覆盖（空值不覆盖）
 *
 * @param {object} common   语言文件里的 common
 * @param {string} brandKey 品牌名（URL 参数 appname 原值）
 * @returns {object} 扁平化后的 common
 */
export function flattenBrandCommon(common = {}, brandKey) {
  const keys = Object.keys(brandAliases)
  const hasBrandBlocks = keys.some(k => common[k] && typeof common[k] === 'object')
  if (!hasBrandBlocks) return common

  const flat = {}
  Object.keys(common).forEach(k => {
    if (keys.indexOf(k) === -1) flat[k] = common[k]
  })

  const base = common[defaultBrand] || {}
  const picked = common[matchBrand(brandKey) || defaultBrand] || {}
  Object.keys(base).forEach(k => {
    if (base[k]) flat[k] = base[k]
  })
  Object.keys(picked).forEach(k => {
    if (picked[k]) flat[k] = picked[k]
  })
  return flat
}

export default brandAliases
